import { Handle, type NodeProps, Position } from "@xyflow/react";
import { BookOpen, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Text } from "@/components/Text";
import { wikiPagePath } from "../KnowledgeTabs";
import { type ConnectionNode, HANDLES } from "./connectionsGraph";

const HANDLE = "!size-1.5 !min-h-0 !min-w-0 !border-0 !bg-content-tertiary";

/** One page in the connections graph; opens it (the page in the middle is where you are). */
export function PageNodeView({ data }: NodeProps<ConnectionNode>) {
  const { t } = useTranslation("knowledge", { keyPrefix: "connections" });
  const navigate = useNavigate();
  const body = (
    <>
      {data.elsewhere ? (
        <Globe size={14} aria-hidden className="shrink-0 text-content-tertiary" />
      ) : (
        <BookOpen size={14} aria-hidden className="shrink-0 text-content-tertiary" />
      )}
      <span className="min-w-0">
        <Text as="span" variant="body-sm" weight={data.center ? "semibold" : "regular"} className="block truncate">
          {data.title}
        </Text>
        {data.elsewhere && (
          <Text as="span" variant="caption" tone="tertiary" className="block">
            {t("elsewhere")}
          </Text>
        )}
      </span>
    </>
  );
  const box = "flex w-[220px] items-center gap-2 rounded-lg border border-border-strong bg-surface-primary px-3 py-2";
  return (
    <>
      <Handle type="target" id={HANDLES.leftIn} position={Position.Left} className={HANDLE} isConnectable={false} />
      <Handle type="source" id={HANDLES.leftOut} position={Position.Left} className={HANDLE} isConnectable={false} />
      {data.center ? (
        <div className={`${box} shadow-sm`} aria-current="page">
          {body}
        </div>
      ) : (
        <button
          type="button"
          className={`${box} nodrag nopan pointer-events-auto text-left transition-colors hover:bg-interactive-hover btn-focus-ring`}
          aria-label={t("open", { title: data.title })}
          onClick={() => navigate(wikiPagePath(data.spaceUuid, data.path))}
        >
          {body}
        </button>
      )}
      <Handle type="target" id={HANDLES.rightIn} position={Position.Right} className={HANDLE} isConnectable={false} />
      <Handle type="source" id={HANDLES.rightOut} position={Position.Right} className={HANDLE} isConnectable={false} />
    </>
  );
}
